import type React from 'react'
import { useCallback, useEffect, useRef } from 'react'
import { dispatchPhotoTrayPreviewEscapeDismiss } from '@/lib/photos/photo-tray-hover-preview'
import type { PhotosPanelEscapeStateRef } from '../../photos-panel/photos-panel'
import { RIGHT_PANEL_IDS } from '../constants/panel-ids'

interface UsePhotosEscapeChainInput {
  isOpen: boolean
  activePanel: string | null
  closePanel: () => void
  railButtonRefs: React.MutableRefObject<Record<string, HTMLButtonElement | null>>
  onEscapeClearPhotoSelectionAndCloseLeftPanel?: () => boolean
  onEscapeFallbackToSelectTool?: () => void
}

/**
 * WMPCM-11088 — the Photos panel's Escape priority chain. Same dropdown-ref pattern as
 * `useEscapeClosesPanel`, plus the Filters-section state `PhotosPanel` reports through
 * `escapeStateRef`. Returns both, for `PhotosPanel`'s props.
 */
export function usePhotosEscapeChain({
  isOpen,
  activePanel,
  closePanel,
  railButtonRefs,
  onEscapeClearPhotoSelectionAndCloseLeftPanel,
  onEscapeFallbackToSelectTool,
}: UsePhotosEscapeChainInput) {
  const photosDropdownOpenRef = useRef(false)

  const handlePhotosDropdownOpenChange = useCallback((open: boolean) => {
    photosDropdownOpenRef.current = open
  }, [])

  const photosEscapeStateRef: PhotosPanelEscapeStateRef = useRef({
    filtersExpanded: false,
    collapseFilters: () => undefined,
  })

  // WMPCM-11088 — Photos panel Escape priority: hover preview / album / topics / tags / sort
  // popovers first (Radix + `onAnyDropdownOpenChange`), then deselect a canvas photo + collapse
  // the left Properties panel, then collapse an expanded Filters section, then close Photos and
  // focus the Photos rail icon (Select tool activates on a second Escape while that icon is focused).
  useEffect(() => {
    if (!isOpen || activePanel !== RIGHT_PANEL_IDS.photos) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (e.defaultPrevented) return

      if (photosDropdownOpenRef.current) {
        e.preventDefault()
        e.stopPropagation()
        dispatchPhotoTrayPreviewEscapeDismiss()
        return
      }

      if (onEscapeClearPhotoSelectionAndCloseLeftPanel?.()) {
        e.preventDefault()
        return
      }

      const escapeState = photosEscapeStateRef.current

      if (escapeState.filtersExpanded) {
        e.preventDefault()
        escapeState.collapseFilters()
        return
      }

      e.preventDefault()
      closePanel()
      setTimeout(() => railButtonRefs.current[RIGHT_PANEL_IDS.photos]?.focus(), 0)
    }

    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [
    isOpen,
    activePanel,
    closePanel,
    railButtonRefs,
    onEscapeClearPhotoSelectionAndCloseLeftPanel,
  ])

  // WMPCM-11088 — second Escape while the Photos rail icon is focused (panel already closed)
  // falls back to the Select tool.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (isOpen && activePanel === RIGHT_PANEL_IDS.photos) return

      if (document.activeElement !== railButtonRefs.current[RIGHT_PANEL_IDS.photos]) return

      e.preventDefault()
      onEscapeFallbackToSelectTool?.()
    }

    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [isOpen, activePanel, railButtonRefs, onEscapeFallbackToSelectTool])

  return { handlePhotosDropdownOpenChange, photosEscapeStateRef }
}
